// The Privacy Policy is authored in legal/politica-de-privacidad.md and stored here
// verbatim as Markdown, so the published document always matches the signed source.
export const PRIVACY_POLICY_VERSION = "2026-08-13";
export const PRIVACY_POLICY_EFFECTIVE_DATE = "13 de agosto de 2026";
export const PRIVACY_POLICY_LAST_UPDATED = "13 de agosto de 2026";

export const PRIVACY_POLICY_MARKDOWN = `
# Política de Privacidad y Tratamiento de Datos Personales de Voxara

**Fecha de entrada en vigor:** 13 de agosto de 2026
**Última actualización:** 13 de agosto de 2026
**Versión:** 2.0

---

## 1. Responsable del Tratamiento

| | |
|---|---|
| **Razón social** | VOXARA S.A.S. |
| **NIT** | 902.061.732-6 |
| **Domicilio** | Carrera 71 # 127C-43, Bogotá D.C., Colombia |
| **Correo de contacto** | lauradramirezabogada@gmail.com |
| **Teléfono** | +57 320 408 6741 |

Esta Política se expide en cumplimiento de la **Ley 1581 de 2012**, el **Decreto 1074 de 2015** (Título 2, Capítulo 25) y demás normas colombianas sobre protección de datos personales, y constituye la política de tratamiento de la información exigida por dicha normativa.

Al acceder o utilizar Voxara, y previa autorización expresa otorgada en el momento del registro, usted acepta las prácticas descritas en esta Política.

---

## 2. Alcance y rol de las partes

Voxara actúa en dos roles distintos según el dato:

- **Como Responsable del Tratamiento** respecto de los datos de su cuenta: nombre, correo electrónico, datos de facturación, registros de uso y de seguridad.
- **Como Encargado del Tratamiento** respecto de los datos personales de terceros contenidos en los archivos que usted carga (voces, nombres, imágenes y demás datos de los participantes de sus grabaciones). En ese caso el Responsable es usted, y Voxara trata esa información únicamente siguiendo sus instrucciones.

Esto significa que **obtener la autorización de las personas que aparecen en sus grabaciones es su responsabilidad**, no la nuestra.

---

## 3. Información que recopilamos

**a. Información que usted nos proporciona:** nombre, dirección de correo electrónico, contraseña (almacenada de forma cifrada e irreversible), información de perfil, mensajes enviados a soporte, contenido que carga a la Plataforma y consultas hechas al asistente de IA.

**b. Información generada durante el uso:** transcripciones, resúmenes, marcas de tiempo, identificación de hablantes, momentos clave, historial de chat con el asistente, metadatos técnicos del archivo y del procesamiento, registros de uso, eventos, errores y actividad de la cuenta.

**c. Información recopilada automáticamente:** dirección IP, tipo de navegador y dispositivo, sistema operativo, idioma, páginas visitadas, fechas y horas de acceso, identificadores de sesión, cookies y tecnologías similares.

**d. Información de pago:** procesada por MercadoPago. **Voxara no recibe ni almacena el número completo de su tarjeta ni su código de seguridad.** Solo conservamos los últimos dígitos, la marca, el identificador de la transacción y los datos de facturación necesarios para expedir la factura electrónica ante la DIAN.

### Datos sensibles

Sus archivos de audio y video pueden contener **datos sensibles** en los términos del artículo 5 de la Ley 1581 de 2012 (salud, origen étnico, convicciones políticas o religiosas, orientación sexual, entre otros), así como la voz y la imagen de las personas grabadas.

Voxara no solicita ni requiere datos sensibles para prestar el servicio. Si usted decide cargar contenido que los incluya, debe contar previamente con la **autorización explícita** de los titulares. Recuerde que, tratándose de datos sensibles, ningún titular está obligado a autorizar su tratamiento.

**Voxara no realiza identificación biométrica de voz ni de rostro.** La función de "identificación de hablantes" separa y etiqueta intervenciones dentro de un mismo archivo; no compara la voz contra ninguna base de datos ni permite reconocer a una persona en archivos distintos.

---

## 4. Finalidades del tratamiento

Tratamos su información para:

- crear y administrar su cuenta y autenticar su acceso;
- prestar, operar y mantener Voxara;
- procesar archivos de video, audio y texto;
- generar transcripciones, resúmenes, respuestas del asistente de IA y exportaciones;
- responder preguntas hechas por usted dentro de la Plataforma;
- procesar pagos, suscripciones y expedir facturación electrónica;
- enviar comunicaciones de servicio, seguridad o soporte;
- detectar fraude, abuso, actividad no autorizada o riesgos de seguridad;
- mejorar precisión, rendimiento y estabilidad del servicio, **sobre datos agregados y anonimizados** conforme a la sección 9;
- cumplir obligaciones legales, contractuales o regulatorias, incluidas las tributarias.

**No usamos el contenido de sus archivos ni sus conversaciones con el asistente para entrenar modelos de inteligencia artificial**, ni propios ni de terceros.

Solo enviaremos comunicaciones comerciales o promocionales si usted otorga una autorización separada para ello, la cual puede revocar en cualquier momento sin afectar el servicio.

---

## 5. Autorización y base legal

En Colombia, el tratamiento requiere **autorización previa, expresa e informada** del titular (artículo 9, Ley 1581 de 2012). Esa autorización se recoge en el formulario de registro mediante una casilla específica, no marcada por defecto, separada del botón de creación de cuenta.

Conservamos prueba de su autorización, incluyendo la fecha y hora, la versión de esta Política aceptada y la dirección IP desde la que se otorgó. Usted puede solicitar copia de dicha prueba en cualquier momento.

Además de su autorización, los siguientes tratamientos se apoyan en:

- **la ejecución del contrato** entre usted y Voxara (prestación del servicio, facturación);
- **el cumplimiento de obligaciones legales** (conservación tributaria y contable, atención de requerimientos de autoridad);
- **el interés legítimo** en la seguridad de la Plataforma (prevención de fraude y abuso).

Usted puede **revocar su autorización** en cualquier momento conforme a la sección 11. La revocación no afecta la licitud del tratamiento previo ni los datos que debamos conservar por mandato legal.

---

## 6. Proveedores externos y encargados

Para operar Voxara compartimos información con los siguientes proveedores, que actúan como Encargados del Tratamiento bajo instrucciones contractuales y obligaciones de confidencialidad:

| Proveedor | Función | Datos a los que accede | País |
|---|---|---|---|
| **Groq, Inc.** | Transcripción (Whisper large-v3) y modelos de lenguaje para resúmenes, chat y análisis | Audio extraído del archivo, transcripciones y consultas del asistente | Estados Unidos |
| **OpenAI, L.L.C.** | Procesamiento de lenguaje natural en funciones seleccionadas | Transcripciones y consultas del asistente | Estados Unidos |
| **Cloudflare, Inc. (R2)** | Almacenamiento de archivos durante el procesamiento | Archivos de video y audio cargados | Infraestructura global |
| **MongoDB, Inc. (Atlas)** | Base de datos de la aplicación | Datos de cuenta, transcripciones, resultados e historial | «REGIÓN DEL CLÚSTER» |
| **MercadoPago** | Procesamiento de pagos y suscripciones | Nombre, correo, datos de facturación y de la transacción | Colombia / Argentina |
| **Resend, Inc.** | Envío de correos transaccionales | Nombre y correo electrónico | Estados Unidos |
| **HostGator** (Newfold Digital, Inc.) | Alojamiento e infraestructura web | Tráfico del sitio y datos en tránsito | Estados Unidos |

Con cada uno de estos proveedores mantenemos acuerdos de tratamiento de datos que les prohíben usar la información para fines propios y, en el caso de los proveedores de inteligencia artificial, **usar su contenido para entrenar sus modelos**.

Los proveedores de IA pueden conservar temporalmente los datos enviados con fines de detección de abuso, por periodos que no superan los treinta (30) días, tras los cuales los eliminan.

Actualizaremos esta tabla cuando cambien nuestros proveedores. Puede solicitar la versión vigente en cualquier momento a lauradramirezabogada@gmail.com.

### Otros casos en que compartimos información

- **Cumplimiento legal:** para atender requerimientos de autoridades competentes. Cuando la ley lo permita, se lo notificaremos antes de entregar la información.
- **Operaciones corporativas:** en caso de fusión, adquisición, reorganización o venta de activos, informándoselo previamente y manteniendo las mismas garantías.
- **Con su autorización:** cuando usted nos lo instruya expresamente.

**No vendemos su información personal ni la compartimos con anunciantes o intermediarios de datos.**

---

## 7. Transferencias internacionales

Algunos de nuestros proveedores están ubicados fuera de Colombia, principalmente en Estados Unidos. Esto implica una transferencia internacional de datos en los términos del artículo 26 de la Ley 1581 de 2012.

Estas transferencias se amparan en:

- su **autorización expresa e inequívoca**, otorgada al registrarse tras ser informado de esta circunstancia; y
- **acuerdos de transmisión de datos** suscritos con cada proveedor, que les imponen las obligaciones de la ley colombiana, incluidas medidas de seguridad y limitación de finalidad.

Si no desea que su información se procese fuera de Colombia, no podremos prestarle el servicio, ya que el procesamiento de IA depende de esta infraestructura.

---

## 8. Retención y eliminación de datos

Conservamos cada categoría de información por el tiempo indicado a continuación:

| Categoría | Plazo de conservación | Motivo |
|---|---|---|
| **Archivos de video y audio originales** | Se eliminan de nuestro almacenamiento **al finalizar el procesamiento**; el borrado alcanza tanto el almacenamiento temporal como Cloudflare R2 | Ya no son necesarios una vez generada la transcripción |
| **Transcripciones, resúmenes y resultados** | Mientras la cuenta esté activa, o hasta que usted elimine el video | Prestación del servicio y acceso continuo |
| **Historial de chat con el asistente** | Mientras exista el video asociado | Continuidad de la conversación |
| **Datos de cuenta y perfil** | Mientras la cuenta esté activa; se eliminan dentro de los **treinta (30) días** siguientes a su solicitud de eliminación o cierre | Periodo de gracia por cierre accidental |
| **Registros técnicos y de seguridad (logs)** | «90» días | Detección de fraude, abuso e incidentes |
| **Registros de autorización de tratamiento** | Mientras dure la relación y cinco (5) años más | Demostrar el cumplimiento ante la autoridad |
| **Facturación y soportes contables** | Cinco (5) años desde el cierre del ejercicio | Artículo 28 del Código de Comercio y normativa tributaria |
| **Copias de seguridad** | Se sobrescriben en el ciclo de rotación, máximo «35» días | Continuidad operativa |

Cuando usted elimina un video o su cuenta, los datos dejan de estar disponibles de inmediato y se eliminan de nuestros sistemas activos. Las copias que permanezcan en respaldos se eliminan al completarse el ciclo de rotación indicado.

Vencidos los plazos, la información se elimina de forma segura o se anonimiza de manera irreversible.

---

## 9. Anonimización y datos agregados

Para medir el uso del producto y mejorar su rendimiento utilizamos **datos agregados y anonimizados**: estadísticas que no permiten identificar a ninguna persona, ni directa ni indirectamente, y que no pueden revertirse a los datos originales. Estos datos dejan de ser datos personales y quedan fuera del alcance de la Ley 1581 de 2012.

Ejemplos: número de archivos procesados por mes, duración promedio, tasas de error, tiempos de respuesta.

Cuando por razones técnicas conservamos un identificador que permitiría vincular la información con una persona (**seudonimización**), la tratamos en todo momento como dato personal, con las mismas garantías y plazos que el resto de esta Política. No la llamamos ni la presentamos como información anónima.

**No analizamos el contenido de sus transcripciones para elaborar perfiles suyos ni para fines distintos de prestarle el servicio.**

---

## 10. Seguridad de la información

Aplicamos medidas administrativas, técnicas y organizativas razonables, entre ellas:

- cifrado en tránsito mediante TLS;
- cifrado en reposo del almacenamiento de archivos y de la base de datos;
- almacenamiento de contraseñas mediante funciones de derivación irreversibles;
- control de acceso por roles y principio de mínimo privilegio;
- registro y monitoreo de eventos de seguridad;
- eliminación automática de los archivos originales al terminar el procesamiento.

Ningún sistema es completamente seguro y no podemos garantizar seguridad absoluta. Usted también es responsable de proteger sus credenciales y usar contraseñas seguras y únicas.

### Notificación de incidentes

Si se produce un incidente que afecte sus datos personales, lo reportaremos a la **Superintendencia de Industria y Comercio** conforme a la normativa vigente y se lo notificaremos al correo de su cuenta **sin dilación indebida**, describiendo qué ocurrió, qué datos se vieron afectados y qué medidas puede tomar.

---

## 11. Sus derechos como titular

Conforme al artículo 8 de la Ley 1581 de 2012, usted tiene derecho a:

- **conocer** los datos personales que tenemos sobre usted y acceder a ellos de forma gratuita;
- **actualizar y rectificar** datos inexactos, incompletos o desactualizados;
- **solicitar prueba** de la autorización que otorgó;
- **ser informado** sobre el uso que hemos dado a sus datos;
- **revocar la autorización** y **solicitar la supresión** de sus datos, cuando no exista un deber legal o contractual de conservarlos;
- **presentar quejas** ante la Superintendencia de Industria y Comercio;
- **solicitar la portabilidad** de su información en un formato estructurado y de uso común;
- **oponerse** a determinados tratamientos, como las comunicaciones comerciales.

### Cómo ejercerlos

Escriba a **lauradramirezabogada@gmail.com** desde el correo asociado a su cuenta, indicando su solicitud. Podemos pedir verificación adicional de identidad antes de procesar ciertas solicitudes.

Muchos de estos derechos también puede ejercerlos directamente desde su cuenta: editar su perfil, eliminar videos y transcripciones, exportar sus resultados y solicitar la eliminación de la cuenta.

### Plazos de respuesta

| Tipo de solicitud | Plazo |
|---|---|
| **Consultas** (acceso, información) | 10 días hábiles, prorrogables por 5 más con aviso previo |
| **Reclamos** (corrección, supresión, revocatoria, incumplimiento) | 15 días hábiles, prorrogables por 8 más con aviso previo |

Si su reclamo está incompleto, se lo comunicaremos dentro de los cinco (5) días siguientes para que lo subsane. Si transcurren dos (2) meses sin respuesta suya, se entenderá desistido.

### Reclamación ante la autoridad

Antes de acudir a la Superintendencia de Industria y Comercio, la ley exige que agote el trámite de consulta o reclamo ante nosotros. Si no queda conforme con nuestra respuesta, o si no le respondemos en los plazos indicados, puede presentar su queja ante la **SIC** a través de sus canales oficiales.

---

## 12. Menores de edad

Voxara **no está dirigida a menores de 18 años** y no permitimos su registro de forma consciente.

El artículo 7 de la Ley 1581 de 2012 y la Sentencia C-748 de 2011 restringen el tratamiento de datos de niños, niñas y adolescentes. Si detectamos que hemos recopilado datos de un menor sin la autorización de su representante legal, procederemos a eliminarlos.

Si usted es padre, madre o representante legal y considera que un menor a su cargo nos ha proporcionado datos personales, escriba a lauradramirezabogada@gmail.com.

Nota: si usted carga contenido en el que aparecen menores de edad, es su responsabilidad contar con la autorización de sus representantes legales.

---

## 13. Cookies y tecnologías similares

Voxara usa cookies, almacenamiento local y tecnologías similares para:

- **cookies estrictamente necesarias:** mantener su sesión iniciada, recordar preferencias y reforzar la seguridad. Sin ellas la Plataforma no funciona;
- **cookies analíticas:** entender el tráfico y el uso de forma agregada. Estas requieren su consentimiento y puede rechazarlas sin perder funcionalidad.

Puede ajustar sus preferencias desde el aviso de cookies de la Plataforma o desde su navegador. Desactivar las cookies necesarias impedirá el uso del servicio.

No usamos cookies de publicidad ni de seguimiento entre sitios.

---

## 14. Contenido confidencial o sensible

Usted es responsable de asegurarse de tener base legal y autorización suficiente antes de cargar contenido que incluya datos personales sensibles, información médica, grabaciones privadas, información confidencial, secretos comerciales o material sujeto a reserva legal o contractual.

Voxara no garantiza que la Plataforma sea adecuada para todos los contextos regulatorios sin configuración adicional, acuerdos específicos o medidas complementarias. Si necesita un acuerdo de tratamiento de datos por escrito para su organización, escriba a lauradramirezabogada@gmail.com.

---

## 15. Enlaces y servicios de terceros

La Plataforma puede contener integraciones o enlaces a sitios, productos o servicios de terceros. No controlamos sus prácticas de privacidad y le recomendamos revisar sus políticas de forma independiente.

---

## 16. Registro Nacional de Bases de Datos

«Cuando aplique según los criterios de la SIC: Las bases de datos administradas por Voxara se encuentran inscritas en el Registro Nacional de Bases de Datos (RNBD) administrado por la Superintendencia de Industria y Comercio.»

---

## 17. Cambios a esta Política

Podemos actualizar esta Política ocasionalmente. Cuando el cambio sea sustancial —por ejemplo, una nueva finalidad, un nuevo proveedor de IA o un cambio en los plazos de retención— se lo notificaremos al correo de su cuenta con al menos **quince (15) días** de anticipación, y cuando la ley lo exija le solicitaremos una nueva autorización.

Publicaremos siempre la fecha de última actualización y conservaremos las versiones anteriores, que puede solicitar en cualquier momento.

---

## 18. Contacto

**VOXARA S.A.S.** — NIT 902.061.732-6
Carrera 71 # 127C-43, Bogotá D.C., Colombia

- Correo (soporte y ejercicio de derechos): **lauradramirezabogada@gmail.com**
- Teléfono: +57 320 408 6741

Autoridad de control: Superintendencia de Industria y Comercio (SIC), Bogotá D.C., Colombia.
`;
